import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { styles } from "../styles";
import { displayName, hasCapability } from "../utils/capabilities";
import BackButton from "../components/BackButton";
import { projectedAnnualPrices } from "../utils/contracts";

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const STATUS_LABEL = {
  scheduled: "Scheduled",
  unassigned: "Active · Unassigned",
  assigned: "Active · Assigned",
  in_progress: "In progress",
  closed: "Completed",
  canceled: "Cancelled",
  skipped: "Skipped",
};

const STATUS_COLOR = {
  scheduled: { bg: "#e0e9f7", fg: "#1a5fb4" },
  unassigned: { bg: "#fff3cd", fg: "#7a5400" },
  assigned: { bg: "#fff3cd", fg: "#7a5400" },
  in_progress: { bg: "#cfe2ff", fg: "#1a5fb4" },
  closed: { bg: "#d4edda", fg: "#1a5f1f" },
  canceled: { bg: "#f5c6cb", fg: "#7a1f1f" },
  skipped: { bg: "#e2e3e5", fg: "#444" },
};

const VISITS_PER_YEAR = {
  monthly: 12,
  quarterly: 4,
  biannual: 2,
  annual: 1,
};

const CANCEL_REASONS = [
  "Customer no-show",
  "Equipment removed",
  "Building inaccessible",
  "Customer terminated contract",
  "Other",
];

const SKIP_REASONS = [
  "Customer no-show",
  "Equipment removed",
  "Building inaccessible",
  "Other",
];

/**
 * Drill-down view of one maintenance contract. Shows the header summary,
 * full visit timeline with per-visit actions (Activate / Reschedule /
 * Skip / View Job), and a bills rollup pulled from the visit jobs.
 */
export default function ContractDetailPage({
  contract,
  jobsRaw = [],
  customers = [],
  actions,
  role,
  capabilities,
  orgId,
  maintenanceDefaults,
  onSelectJob,
  onBack,
}) {
  const canManage = hasCapability(role, capabilities, "editJobs");

  // Org users — only loaded when there's a defaultTechId to display, so
  // contracts without one don't pay the listener cost.
  const [orgUsers, setOrgUsers] = useState([]);
  useEffect(() => {
    if (!orgId || !contract?.defaultTechId) return;
    const q = query(collection(db, "users"), where("orgId", "==", orgId));
    const unsub = onSnapshot(q, (snap) =>
      setOrgUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [orgId, contract?.defaultTechId]);
  const defaultTech = orgUsers.find((u) => u.id === contract?.defaultTechId);

  // Effective bill automation = explicit per-contract override OR fall
  // back to the org default. The override UI writes one of the three
  // literal values: "inherit" (= use org default), "off", "auto-draft".
  // "inherit" is the on-disk representation of "use org default" -- we
  // only persist this when the admin actively picks it. Anything else
  // missing/null also falls back to org default.
  const orgDefault = maintenanceDefaults?.billAutomation || "off";
  const explicit = contract.billAutomation;
  const effectiveBillAutomation =
    explicit === "off" || explicit === "auto-draft" ? explicit : orgDefault;

  const handleBillAutomationChange = async (value) => {
    if (!canManage) return;
    try {
      await actions.updateContract(contract.id, {
        billAutomation:
          value === "off" || value === "auto-draft" ? value : "inherit",
      });
    } catch (e) {
      window.alert(`Couldn't save: ${e.message}`);
    }
  };

  const customer = customers.find((c) => c.id === contract.customerId);
  const location = customer?.locations?.find(
    (l) => l.id === contract.locationId
  );
  const equipmentById = useMemo(() => {
    const m = new Map();
    for (const c of customers)
      for (const l of c.locations || [])
        for (const e of l.equipment || []) m.set(e.id, e);
    return m;
  }, [customers]);

  // All visit jobs for this contract, sorted by visitIndex.
  const visits = useMemo(
    () =>
      jobsRaw
        .filter((j) => j.contractId === contract.id)
        .sort((a, b) => (a.visitIndex || 0) - (b.visitIndex || 0)),
    [jobsRaw, contract.id]
  );

  // Rollup: total billed across all bills on every visit.
  const billedToDate = useMemo(() => {
    let total = 0;
    for (const v of visits) {
      const bills = v.bills || [];
      for (const b of bills) total += Number(b.grandTotal || 0);
      if (v.billFinalized && !bills.length) {
        // Legacy single-bill visit (no progress billing used) — fall back
        // to costing total if present.
        const c = v.costing || {};
        const sub =
          (c.labor || []).reduce(
            (s, l) =>
              s + Number(l.hours || 0) * Number(l.rate || 0),
            0
          ) +
          (c.materials || []).reduce(
            (s, m) =>
              s +
              Number(m.qty || 0) *
                Number(m.unitCost || 0) *
                (1 + Number(m.markup || 0) / 100),
            0
          );
        total += sub;
      }
    }
    return total;
  }, [visits]);

  const totalVisits = visits.length;
  const completedVisits = visits.filter((v) => v.status === "closed").length;
  const skippedVisits = visits.filter((v) => v.status === "skipped").length;
  const visitsPerYear =
    VISITS_PER_YEAR[contract.visitInterval] || 4;
  const pricePerVisit =
    visitsPerYear > 0 ? Number(contract.annualPrice || 0) / visitsPerYear : 0;
  const contractValue =
    Number(contract.annualPrice || 0) * (contract.termYears || 1);

  const onActivate = (visit) => {
    if (!actions?.activateVisit) return;
    actions.activateVisit(visit.id);
  };

  const onReschedule = (visit) => {
    const next = window.prompt(
      `Reschedule Visit ${visit.visitIndex} from ${
        visit.scheduledDate || "?"
      } to new date (YYYY-MM-DD):`,
      visit.scheduledDate || ""
    );
    if (!next) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(next.trim())) {
      window.alert("Date must be in YYYY-MM-DD format.");
      return;
    }
    const note = window.prompt("Reason / note (optional):", "") || "";
    actions.rescheduleVisit(visit.id, next.trim(), note);
  };

  const onSkip = (visit) => {
    const reason = window.prompt(
      `Skip Visit ${visit.visitIndex}. Pick a reason or type one:\n` +
        SKIP_REASONS.map((r, i) => `${i + 1}. ${r}`).join("\n"),
      "Customer no-show"
    );
    if (!reason) return;
    const ok = window.confirm(
      `Mark Visit ${visit.visitIndex} skipped with reason "${reason}"? In per-visit billing mode, skipped visits don't generate a bill.`
    );
    if (!ok) return;
    actions.skipVisit(visit.id, reason);
  };

  const onCancelContract = () => {
    const reason = window.prompt(
      `Cancel contract ${contract.contractNumber || ""}? Pick a reason or type one:\n` +
        CANCEL_REASONS.map((r, i) => `${i + 1}. ${r}`).join("\n"),
      "Customer terminated contract"
    );
    if (!reason) return;
    const ok = window.confirm(
      `Cancel contract with reason "${reason}"? Visits already completed stay as-is. Future "scheduled" visits should be skipped or cancelled separately.`
    );
    if (!ok) return;
    actions.cancelContract(contract.id, reason);
  };

  const isCancelled = contract.status === "cancelled";

  return (
    <>
      <BackButton onClick={onBack} />
      <h2 style={{ margin: "8px 0 4px" }}>
        {contract.contractNumber || ""}
        {contract.title ? ` — ${contract.title}` : ""}
      </h2>
      <div style={{ fontSize: "13px", color: "#666", marginBottom: "12px" }}>
        {customer?.name || "—"} · {location?.name || "—"} ·{" "}
        <strong>{STATUS_LABEL[contract.status] || contract.status}</strong>
      </div>

      {/* --- Contract summary card --- */}
      <div style={styles.card}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "10px 16px",
            fontSize: "13px",
          }}
        >
          <div>
            <div style={{ color: "#666", fontSize: "11px" }}>Term</div>
            <div>
              {contract.startDate || "?"} → {contract.endDate || "?"}{" "}
              ({contract.termYears} yr)
            </div>
          </div>
          <div>
            <div style={{ color: "#666", fontSize: "11px" }}>Visit cadence</div>
            <div>
              {contract.visitInterval} ({visitsPerYear}/yr × {contract.termYears}{" "}
              yr = {totalVisits} visits)
            </div>
          </div>
          <div>
            <div style={{ color: "#666", fontSize: "11px" }}>Billing mode</div>
            <div>{contract.billingMode}</div>
          </div>
          {contract.billingMode === "per-interval" && (
            <div>
              <div style={{ color: "#666", fontSize: "11px" }}>Billing interval</div>
              <div>{contract.billingInterval}</div>
            </div>
          )}
          <div>
            <div style={{ color: "#666", fontSize: "11px" }}>
              {Number(contract.annualEscalationPct) > 0
                ? "Year-1 annual price"
                : "Annual price"}
            </div>
            <div>
              <strong>{money(contract.annualPrice)}/yr</strong>
            </div>
          </div>
          <div>
            <div style={{ color: "#666", fontSize: "11px" }}>
              {Number(contract.annualEscalationPct) > 0
                ? "Year-1 per-visit price"
                : "Per-visit price (per-visit mode)"}
            </div>
            <div>{money(pricePerVisit)}</div>
          </div>
          {Number(contract.annualEscalationPct) > 0 && (
            <div>
              <div style={{ color: "#666", fontSize: "11px" }}>
                Annual escalator
              </div>
              <div>
                <strong>{Number(contract.annualEscalationPct)}%</strong>
              </div>
            </div>
          )}
          {contract.defaultTechId && (
            <div>
              <div style={{ color: "#666", fontSize: "11px" }}>
                Default tech
              </div>
              <div>
                <strong>
                  {defaultTech
                    ? displayName(defaultTech.profile, defaultTech.email)
                    : "(loading…)"}
                </strong>
              </div>
            </div>
          )}
          <div>
            <div style={{ color: "#666", fontSize: "11px" }}>Contract value</div>
            <div>{money(contractValue)}</div>
          </div>
        </div>
        {Number(contract.annualEscalationPct) > 0 && (() => {
          const projection = projectedAnnualPrices(contract);
          const lifetime = projection.reduce((s, y) => s + y.annualPrice, 0);
          return (
            <div
              style={{
                marginTop: "10px",
                padding: "8px 10px",
                background: "#f3f8ff",
                border: "1px solid #d1e2f8",
                borderRadius: "4px",
                fontSize: "12px",
                color: "#1a5fb4",
              }}
            >
              <strong>
                Projected pricing with {Number(contract.annualEscalationPct)}%
                annual escalator:
              </strong>
              <div
                style={{
                  marginTop: "4px",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: "2px 12px",
                }}
              >
                {projection.map((y) => (
                  <div key={y.year}>
                    Year {y.year}: <strong>{money(y.annualPrice)}</strong>{" "}
                    ({money(y.perVisitPrice)}/visit)
                  </div>
                ))}
              </div>
              <div style={{ marginTop: "4px" }}>
                Lifetime contract value with escalator ≈{" "}
                <strong>{money(lifetime)}</strong>
              </div>
            </div>
          );
        })()}

        {/* Per-contract bill automation override. Per-visit contracts
            only -- per-interval calendar-driven auto-draft lands in
            Phase 1.5. */}
        {contract.billingMode === "per-visit" && (
          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              background: "#f6f8fb",
              borderRadius: "6px",
              border: "1px solid #e3e7eb",
            }}
          >
            <div style={{ fontSize: "12px", fontWeight: 700, marginBottom: "6px" }}>
              Bill automation on visit complete
            </div>
            <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
              When a visit gets marked complete, what happens to the bill?
              Defaults to your Company Settings choice (
              <strong>{orgDefault === "auto-draft" ? "Auto-draft" : "Off"}</strong>
              ) unless you override here.
            </div>
            <select
              disabled={!canManage}
              value={explicit || "inherit"}
              onChange={(e) => handleBillAutomationChange(e.target.value)}
              style={{
                padding: "6px 8px",
                fontSize: "13px",
                borderRadius: "4px",
                border: "1px solid #c6cbd0",
              }}
            >
              <option value="inherit">
                Use company default ({orgDefault === "auto-draft" ? "Auto-draft" : "Off"})
              </option>
              <option value="off">Off (manual)</option>
              <option value="auto-draft">
                Auto-draft (pre-fill the per-visit price)
              </option>
            </select>
            <div style={{ fontSize: "11px", color: "#888", marginTop: "6px" }}>
              Effective: <strong>{effectiveBillAutomation === "auto-draft" ? "Auto-draft" : "Off"}</strong>
            </div>
          </div>
        )}

        {contract.scopeOfWork && (
          <div style={{ marginTop: "12px" }}>
            <strong style={{ fontSize: "13px" }}>Scope of work</strong>
            <ul
              style={{
                margin: "4px 0",
                paddingLeft: "20px",
                fontSize: "13px",
              }}
            >
              {contract.scopeOfWork
                .split(/\r?\n/)
                .map((s) => s.trim())
                .filter(Boolean)
                .map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
            </ul>
          </div>
        )}

        {contract.equipmentIds && contract.equipmentIds.length > 0 && (
          <div style={{ marginTop: "8px", fontSize: "13px" }}>
            <strong>Covered equipment:</strong>{" "}
            {contract.equipmentIds
              .map((eqId) => {
                const eq = equipmentById.get(eqId);
                return eq
                  ? `${eq.type}${eq.subType ? ` (${eq.subType})` : ""}`
                  : "(unknown)";
              })
              .join(", ")}
          </div>
        )}

        {contract.status === "cancelled" && (
          <div
            style={{
              marginTop: "10px",
              padding: "8px 10px",
              background: "#f8d7da",
              border: "1px solid #f1aeb5",
              borderRadius: "4px",
              fontSize: "12px",
              color: "#7a1f1f",
            }}
          >
            <strong>Cancelled</strong>
            {contract.cancelledAt
              ? ` ${new Date(contract.cancelledAt).toLocaleDateString()}`
              : ""}
            {contract.cancelReason ? ` · ${contract.cancelReason}` : ""}
          </div>
        )}

        {canManage && !isCancelled && (
          <div style={{ marginTop: "12px" }}>
            <button
              style={{
                ...styles.button,
                background: "#f8d7da",
                color: "#7a1f1f",
              }}
              onClick={onCancelContract}
            >
              Cancel Contract
            </button>
          </div>
        )}
      </div>

      {/* --- Rollup --- */}
      <div style={{ ...styles.card, marginTop: "12px" }}>
        <strong style={{ fontSize: "13px" }}>Status</strong>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "10px",
            marginTop: "8px",
            fontSize: "13px",
          }}
        >
          <div>
            <div style={{ color: "#666", fontSize: "11px" }}>Completed</div>
            <div>
              <strong>
                {completedVisits} / {totalVisits}
              </strong>
            </div>
          </div>
          <div>
            <div style={{ color: "#666", fontSize: "11px" }}>Skipped</div>
            <div>{skippedVisits}</div>
          </div>
          <div>
            <div style={{ color: "#666", fontSize: "11px" }}>Billed to date</div>
            <div style={{ color: "#1a5fb4" }}>
              <strong>{money(billedToDate)}</strong>
            </div>
          </div>
          <div>
            <div style={{ color: "#666", fontSize: "11px" }}>Remaining</div>
            <div style={{ color: "#7a5400" }}>
              {money(Math.max(0, contractValue - billedToDate))}
            </div>
          </div>
        </div>
      </div>

      {/* --- Visit timeline --- */}
      <div style={{ ...styles.card, marginTop: "12px" }}>
        <strong style={{ fontSize: "13px" }}>Visit timeline</strong>
        {visits.length === 0 ? (
          <div
            style={{
              marginTop: "6px",
              fontSize: "12px",
              color: "#888",
            }}
          >
            No visits found for this contract yet.
          </div>
        ) : (
          visits.map((v) => {
            const status = STATUS_COLOR[v.status] || STATUS_COLOR.scheduled;
            const billsTotal = (v.bills || []).reduce(
              (s, b) => s + Number(b.grandTotal || 0),
              0
            );
            return (
              <div
                key={v.id}
                style={{
                  borderTop: "1px solid #eee",
                  marginTop: "6px",
                  paddingTop: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  flexWrap: "wrap",
                  fontSize: "13px",
                }}
              >
                <div style={{ flex: 1, minWidth: "220px" }}>
                  <div style={{ fontWeight: 600 }}>
                    Visit {v.visitIndex} · {v.scheduledDate || "?"}{" "}
                    <span
                      style={{
                        marginLeft: "6px",
                        fontSize: "10px",
                        padding: "1px 6px",
                        borderRadius: "3px",
                        textTransform: "uppercase",
                        background: status.bg,
                        color: status.fg,
                      }}
                    >
                      {STATUS_LABEL[v.status] || v.status}
                    </span>
                  </div>
                  <div style={{ fontSize: "11px", color: "#666" }}>
                    Job #{v.jobNumber || "—"}
                    {billsTotal > 0
                      ? ` · billed ${money(billsTotal)}`
                      : ""}
                  </div>
                  {v.skipReason && (
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#7a1f1f",
                        fontStyle: "italic",
                      }}
                    >
                      Reason: {v.skipReason}
                    </div>
                  )}
                </div>
                {canManage && v.status === "scheduled" && (
                  <>
                    <button
                      style={{ ...styles.button, padding: "4px 8px" }}
                      onClick={() => onActivate(v)}
                      title="Move to the active job queue (becomes unassigned)"
                    >
                      Activate
                    </button>
                    <button
                      style={{ ...styles.button, padding: "4px 8px" }}
                      onClick={() => onReschedule(v)}
                    >
                      Reschedule
                    </button>
                    <button
                      style={{ ...styles.button, padding: "4px 8px" }}
                      onClick={() => onSkip(v)}
                    >
                      Skip
                    </button>
                  </>
                )}
                {canManage &&
                  ["unassigned", "assigned", "in_progress"].includes(
                    v.status
                  ) && (
                    <>
                      <button
                        style={{ ...styles.button, padding: "4px 8px" }}
                        onClick={() => onReschedule(v)}
                      >
                        Reschedule
                      </button>
                      <button
                        style={{ ...styles.button, padding: "4px 8px" }}
                        onClick={() => onSkip(v)}
                      >
                        Skip
                      </button>
                    </>
                  )}
                <button
                  style={{ ...styles.button, padding: "4px 8px" }}
                  onClick={() => onSelectJob && onSelectJob(v)}
                >
                  View Job
                </button>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
